import includes from 'lodash/includes';
import times from 'lodash/times';
import without from 'lodash/without';
import { func } from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import api from 'atg/api/API';
import BetItemInfo from 'atg/components/dialog/archive/BetItemInfo';
import BetItemType from 'atg/components/dialog/archive/BetItemType';
import TemplateItemInfo from 'atg/components/dialog/archive/TemplateItemInfo';
import { ARCHIVE_DELETE_COMPLETED } from 'atg/constants/Events';
import EventDispatcher from 'atg/utils/EventDispatcher';
import ArchiveDialog from 'shared/components/dialog/ArchiveDialog';
import { showAtgTemplateDialog, showConfirmDialog } from 'shared/redux/actions';
import { getText } from 'shared/utils/Text';

const sortByModified = (a, b) =>
  b.getModifiedTime().getTime() - a.getModifiedTime().getTime();

const mapDispatchToProps = dispatch =>
  bindActionCreators({ showConfirmDialog, showAtgTemplateDialog }, dispatch);

export default withRouter(
  connect(
    null,
    mapDispatchToProps,
  )(
    class ArchiveDialogContainer extends Component {
      static propTypes = {
        closeHandler: func.isRequired,
        onOpenBetClick: func.isRequired,
        onDuplicateBetClick: func.isRequired,
      };

      state = {
        selectedCategory: 'futureBets',
        isLoadingBets: false,
        hasLoadedBets: false,
        isLoadingTemplates: false,
        hasLoadedTemplates: false,
        futureBets: [],
        pastBets: [],
        templates: [],
        uploadedBets: [],
        isDeleting: false,
        markedForDeletion: [],
      };

      componentDidMount() {
        this.loadDataForSelectedViewIfNeeded();
        EventDispatcher.addListener(
          ARCHIVE_DELETE_COMPLETED,
          this.onDeleteCompleted,
        );
      }

      componentDidUpdate(_prevProps, prevState) {
        const { selectedCategory: prevCategory, isDeleting: wasDeleting } =
          prevState;

        const { selectedCategory, isDeleting } = this.state;

        if (wasDeleting && !isDeleting) {
          if (
            selectedCategory === 'futureBets' ||
            selectedCategory === 'pastBets' ||
            selectedCategory === 'uploadedBets'
          ) {
            this.loadBetsData();
          } else {
            this.loadTemplateData();
          }
        } else if (prevCategory !== selectedCategory) {
          this.loadDataForSelectedViewIfNeeded();
        }
      }

      componentWillUnmount() {
        EventDispatcher.removeListener(
          ARCHIVE_DELETE_COMPLETED,
          this.onDeleteCompleted,
        );
      }

      loadDataForSelectedViewIfNeeded() {
        const { selectedCategory, hasLoadedBets, hasLoadedTemplates } =
          this.state;
        setTimeout(() => {
          const isBetCategory =
            selectedCategory === 'futureBets' ||
            selectedCategory === 'pastBets' ||
            selectedCategory === 'uploadedBets';
          if (isBetCategory && !hasLoadedBets) {
            this.loadBetsData();
          } else if (!isBetCategory && !hasLoadedTemplates) {
            this.loadTemplateData();
          }
        }, 0);
      }

      loadBetsData() {
        const { isLoadingBets } = this.state;

        if (isLoadingBets) {
          return;
        }

        this.setState({
          isLoadingBets: true,
        });
        api.loadSavedBets((ok, savedBetSet) => {
          const futureBets = times(
            savedBetSet.currentBets.getNumberOfSavedBets(),
            index => savedBetSet.currentBets.getSavedBet(index),
          ).sort(sortByModified);
          const pastBets = times(
            savedBetSet.earlierBets.getNumberOfSavedBets(),
            index => savedBetSet.earlierBets.getSavedBet(index),
          ).sort(sortByModified);
          const uploadedBets = times(
            savedBetSet.uploadedBets.getNumberOfSavedBets(),
            index => savedBetSet.uploadedBets.getSavedBet(index),
          );

          this.setState({
            isLoadingBets: false,
            hasLoadedBets: true,
            futureBets,
            pastBets,
            uploadedBets,
          });
        });
      }

      loadTemplateData() {
        const { isLoadingTemplates } = this.state;

        if (isLoadingTemplates) {
          return;
        }

        this.setState({
          isLoadingTemplates: true,
        });
        api.loadSavedTemplates((ok, templateSet) => {
          this.setState({
            isLoadingTemplates: false,
            hasLoadedTemplates: true,
            templates: times(templateSet.getNumberOfSavedTemplates(), index =>
              templateSet.getSavedTemplate(index),
            ).sort(sortByModified),
          });
        });
      }

      onMenuClick = categoryId => {
        this.setState({
          selectedCategory: categoryId,
        });
      };

      onDeleteCompleted = () => {
        this.setState({
          isDeleting: false,
          markedForDeletion: [],
        });
      };

      onDelete = item => {
        let { markedForDeletion } = this.state;
        markedForDeletion = includes(markedForDeletion, item)
          ? without(markedForDeletion, item)
          : [...markedForDeletion, item];
        this.setState({
          markedForDeletion,
        });
      };

      onFileUploaded = () => {
        this.loadBetsData();
      };

      onCancelDelete = () => {
        this.setState({
          markedForDeletion: [],
        });
      };

      onOpenBet = bet => {
        this.props.closeHandler();
        this.props.onOpenBetClick(bet);
      };

      onDuplicateBet = bet => {
        const { showConfirmDialog } = this.props;

        showConfirmDialog({
          title: getText('dialog-archive-confirm-duplicate'),
          onConfirm: () => {
            this.props.onDuplicateBetClick(bet, () =>
              this.props.closeHandler(),
            );
          },
        });
      };

      onConfirmDeleteBet = () => {
        this.setState({
          isDeleting: true,
        });

        api.deleteSavedBets(
          this.state.markedForDeletion.map(bet => bet.getID()),
        );
      };

      onEditTemplate = template => {
        this.props.showAtgTemplateDialog({
          id: template.getID(),
          name: template.getName(),
          reductionsEnabled: template.getReductions(),
          marksEnabled: template.getMarks(),
          betType: template.getBetType(),
          onSaved: () => {
            this.loadTemplateData(this.state);
          },
        });
      };

      onConfirmDeleteTemplate = () => {
        this.setState({
          isDeleting: true,
        });

        api.deleteTemplates(
          this.state.markedForDeletion.map(template => template.getID()),
        );
      };

      renderBetItemBetType = bet => <BetItemType bet={bet} />;

      renderBetItemInfo = bet => <BetItemInfo bet={bet} />;

      renderTemplateItemInfo = template => (
        <TemplateItemInfo template={template} />
      );

      render() {
        const {
          closeHandler,
          match: { params, path },
        } = this.props;

        const {
          selectedCategory,
          futureBets,
          pastBets,
          templates,
          uploadedBets,
          markedForDeletion,
          isLoadingBets,
          isLoadingTemplates,
          isDeleting,
        } = this.state;

        const isTemplates = selectedCategory === 'templates';

        const map = {
          futureBets: {
            items: futureBets,
            emptyText: getText('dialog-archive-empty-future'),
          },
          pastBets: {
            items: pastBets,
            emptyText: getText('dialog-archive-empty-past'),
          },
          templates: {
            items: templates,
            emptyText: getText('dialog-archive-empty-templates'),
          },
          uploadedBets: {
            items: uploadedBets,
            emptyText: getText('dialog-archive-empty-upload'),
          },
        };

        const items = isTemplates
          ? map[selectedCategory].items.filter(template => !template.isGlobal())
          : map[selectedCategory].items;

        // Get id of bet when we're on the atg bet page
        const activeBetId =
          path === '/trav/:betId'
            ? Number.parseInt(params.betId, 10)
            : undefined;

        const activeBetItem = isTemplates
          ? undefined
          : items.find(item => item.getID() === activeBetId);

        let confirmDeleteTextProp;
        if (isTemplates) {
          confirmDeleteTextProp =
            markedForDeletion.length > 1
              ? 'dialog-archive-templates-confirm-delete-multi'
              : 'dialog-archive-templates-confirm-delete';
        } else {
          confirmDeleteTextProp =
            markedForDeletion.length > 1
              ? 'dialog-archive-confirm-delete-multi'
              : 'dialog-archive-confirm-delete';
        }

        return (
          <ArchiveDialog
            closeHandler={closeHandler}
            isLoading={isLoadingBets || isLoadingTemplates || isDeleting}
            items={items}
            activeBetItem={activeBetItem}
            selectedCategory={selectedCategory}
            categories={[
              {
                id: 'futureBets',
                type: 'bet',
                menuText: getText('dialog-archive-bet-future'),
              },
              {
                id: 'pastBets',
                type: 'bet',
                menuText: getText('dialog-archive-bet-past'),
              },
              {
                id: 'templates',
                type: 'template',
                menuText: getText('dialog-archive-templates'),
              },
              {
                id: 'uploadedBets',
                type: 'bet',
                menuText: getText('dialog-archive-bet-upload'),
              },
            ]}
            markedForDeletion={markedForDeletion}
            bodyText={
              isTemplates ? getText('dialog-archive-templates-info') : undefined
            }
            emptyText={map[selectedCategory].emptyText}
            confirmDeleteText={getText(
              confirmDeleteTextProp,
              markedForDeletion.length,
            )}
            renderItemBox={isTemplates ? undefined : this.renderBetItemBetType}
            renderItemInfo={
              isTemplates ? this.renderTemplateItemInfo : this.renderBetItemInfo
            }
            onMenuClick={this.onMenuClick}
            onEditItemClick={isTemplates ? this.onEditTemplate : undefined}
            onOpenItemClick={isTemplates ? undefined : this.onOpenBet}
            onDuplicateItemClick={isTemplates ? undefined : this.onDuplicateBet}
            onDeleteItemClick={this.onDelete}
            onConfirmDeleteClick={
              isTemplates
                ? this.onConfirmDeleteTemplate
                : this.onConfirmDeleteBet
            }
            onCancelDeleteClick={this.onCancelDelete}
            onFileUploaded={this.onFileUploaded}
          />
        );
      }
    },
  ),
);
